/**
 * AI-referrer detection — identifies when a visitor arrived from an
 * AI assistant (ChatGPT, Perplexity, Claude, Gemini, Copilot, You.com).
 *
 * Used by analytics to:
 *  1. Auto-attach `ai_referrer` to every event (so we can attribute
 *     conversions to AI citations).
 *  2. Fire a one-shot `ai_citation_visit` event per session.
 *
 * Detection sources (in order):
 *  - document.referrer hostname
 *  - utm_source query param (e.g. ?utm_source=chatgpt)
 *  - utm_medium=ai-citation marker
 */

const REFERRER_HOSTS: Array<{ pattern: RegExp; source: string }> = [
  { pattern: /(^|\.)chat\.openai\.com$/i, source: "chatgpt" },
  { pattern: /(^|\.)chatgpt\.com$/i, source: "chatgpt" },
  { pattern: /(^|\.)perplexity\.ai$/i, source: "perplexity" },
  { pattern: /(^|\.)claude\.ai$/i, source: "claude" },
  { pattern: /(^|\.)gemini\.google\.com$/i, source: "gemini" },
  { pattern: /(^|\.)bard\.google\.com$/i, source: "gemini" },
  { pattern: /(^|\.)copilot\.microsoft\.com$/i, source: "copilot" },
  { pattern: /(^|\.)you\.com$/i, source: "you" },
  { pattern: /(^|\.)phind\.com$/i, source: "phind" },
  { pattern: /(^|\.)kagi\.com$/i, source: "kagi" },
];

const UTM_SOURCE_MAP: Record<string, string> = {
  chatgpt: "chatgpt",
  openai: "chatgpt",
  perplexity: "perplexity",
  claude: "claude",
  anthropic: "claude",
  gemini: "gemini",
  bard: "gemini",
  copilot: "copilot",
  "ai-citation": "generic_ai",
};

const STORAGE_KEY = "td_ai_ref";
const VISIT_FIRED_KEY = "td_ai_visit_fired";

export interface AiReferrer {
  source: string;
  detectedAt: string;
  landing_page: string;
  via: "referrer" | "utm";
}

/**
 * Inspect current URL + document.referrer for AI-source signals.
 * Returns null if no AI source detected.
 */
export function detectAiReferrer(): AiReferrer | null {
  if (typeof window === "undefined") return null;

  // 1. UTM source (explicit, wins over referrer)
  try {
    const url = new URL(window.location.href);
    const utmSource = url.searchParams.get("utm_source")?.toLowerCase().trim();
    const utmMedium = url.searchParams.get("utm_medium")?.toLowerCase().trim();
    if (utmSource && UTM_SOURCE_MAP[utmSource]) {
      return {
        source: UTM_SOURCE_MAP[utmSource],
        detectedAt: new Date().toISOString(),
        landing_page: window.location.pathname,
        via: "utm",
      };
    }
    if (utmMedium === "ai-citation") {
      return {
        source: utmSource || "generic_ai",
        detectedAt: new Date().toISOString(),
        landing_page: window.location.pathname,
        via: "utm",
      };
    }
  } catch {
    // ignore
  }

  // 2. Document referrer
  try {
    if (!document.referrer) return null;
    const refUrl = new URL(document.referrer);
    for (const { pattern, source } of REFERRER_HOSTS) {
      if (pattern.test(refUrl.hostname)) {
        return {
          source,
          detectedAt: new Date().toISOString(),
          landing_page: window.location.pathname,
          via: "referrer",
        };
      }
    }
  } catch {
    // ignore
  }

  return null;
}

/**
 * Capture and persist the AI referrer on first detection (first-touch wins).
 * Idempotent — safe to call on every page load.
 */
export function captureAiReferrer(): AiReferrer | null {
  try {
    const existing = sessionStorage.getItem(STORAGE_KEY);
    if (existing) return JSON.parse(existing) as AiReferrer;
  } catch {
    // ignore
  }
  const detected = detectAiReferrer();
  if (detected) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(detected));
    } catch {
      // ignore
    }
  }
  return detected;
}

export function getAiReferrer(): AiReferrer | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AiReferrer) : null;
  } catch {
    return null;
  }
}

/** Has the one-shot ai_citation_visit event already fired this session? */
export function hasFiredAiVisitEvent(): boolean {
  try {
    return sessionStorage.getItem(VISIT_FIRED_KEY) === "1";
  } catch {
    return false;
  }
}

export function markAiVisitEventFired(): void {
  try {
    sessionStorage.setItem(VISIT_FIRED_KEY, "1");
  } catch {
    // ignore
  }
}
